// ============================================================================
// STUDIO BOOKINGS SERVICE
// Frontend API client for advance studio room booking + approval workflow
// ============================================================================

import { api } from './api';

// ============================================================================
// TYPES
// ============================================================================

export type BookingDateType = 'morning' | 'afternoon' | 'full_day' | 'multi_day';
export type BookingBillingType = 'investment' | 'chargeable' | 'na';
export type CustomerStatus = 'existing' | 'new' | 'internal';
export type BookingStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';
export type FacilitatorTier = 'tier1' | 'tier2';
export type OutlookSyncStatus = 'synced' | 'failed' | 'skipped';

export interface StudioBooking {
  bookingId: string;
  roomId: string;
  roomName: string;
  requestedBy: string;
  requestedByName: string;
  requestedByEmail: string;
  facilitatorId: string;
  facilitatorName: string;
  facilitatorTier: FacilitatorTier;
  secondaryFacilitatorIds?: string[];
  secondaryFacilitatorNames?: string[];
  accountTeamLeadEmail?: string;
  sendAccountManagerEmails?: boolean;
  traineeCandidateIds?: string[];
  traineeNames?: string[];
  traineeAttendedIds?: string[];
  traineeAttendanceRecordedAt?: string;
  customer: string;
  sector?: string;
  eventTypeId: string;
  eventTypeName: string;
  isNewCustomer: boolean;
  customerStatus: CustomerStatus;
  billingType: BookingBillingType;
  dateType: BookingDateType;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  goal: string;
  numberOfCustomers?: number;
  numberOfAttendees?: number;
  status: BookingStatus;
  reviewedBy?: string;
  reviewedByName?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  cancelledBy?: string;
  cancelledAt?: string;
  outlookEventId?: string;
  outlookEventsByDate?: Record<string, string>;
  outlookSyncStatus?: OutlookSyncStatus;
  outlookSyncError?: string;
  outlookConflictOverride?: { overriddenBy: string; overriddenByName: string; overriddenAt: string } | null;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
}

export interface EventType {
  eventTypeId: string;
  name: string;
  description?: string;
  isActive: boolean;
  isFacilitatorTraining?: boolean;
  whatToExpect?: string;          // markdown for booking emails
  createdAt: string;
  createdBy: string;
  updatedAt: string;
}

export interface Sector {
  sectorId: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
}

export interface FacilitatorSummary {
  userId: string;
  displayName: string;
  email: string;
  tier: FacilitatorTier;
  certifiedAt?: string;
  isSystemAdmin?: boolean;
  isOffboarded?: boolean;
  homeRoomIds?: string[];
}

export interface ConflictInfo {
  bookingId: string;
  customer: string;
  status: BookingStatus;
  startDate: string;
  endDate: string;
  dateType: BookingDateType;
}

export interface CreateBookingInput {
  roomId: string;
  facilitatorId: string;
  facilitatorName: string;
  facilitatorTier: FacilitatorTier;
  secondaryFacilitatorIds?: string[];
  accountTeamLeadEmail?: string;
  sendAccountManagerEmails?: boolean;
  traineeCandidateIds?: string[];
  customer: string;
  sector?: string;
  eventTypeId: string;
  isNewCustomer: boolean;
  customerStatus: CustomerStatus;
  billingType: BookingBillingType;
  dateType: BookingDateType;
  startDate: string;
  endDate: string;
  goal: string;
  numberOfCustomers?: number;
  numberOfAttendees?: number;
  overrideOutlookConflict?: boolean;
}

// Human-readable slot labels and times
export const DATE_TYPE_LABELS: Record<BookingDateType, string> = {
  morning:   'Morning (09:00–12:30)',
  afternoon: 'Afternoon (13:30–17:00)',
  full_day:  'Full Day (09:00–17:00)',
  multi_day: 'Multi-day'
};

export const STATUS_LABELS: Record<BookingStatus, string> = {
  pending:   'Pending Review',
  approved:  'Approved',
  rejected:  'Rejected',
  cancelled: 'Cancelled'
};

export const STATUS_COLORS: Record<BookingStatus, string> = {
  pending:   'text-amber-600 bg-amber-50 border-amber-200',
  approved:  'text-green-700 bg-green-50 border-green-200',
  rejected:  'text-red-700 bg-red-50 border-red-200',
  cancelled: 'text-gray-500 bg-gray-50 border-gray-200'
};

export const TIER_LABELS: Record<FacilitatorTier, string> = {
  tier1: 'Tier 1',
  tier2: 'Tier 2'
};

// Sentinel facilitatorId values used when a booking has no real facilitator assigned yet
export const FACILITATOR_TBC_ID = '__TBC__';
export const FACILITATOR_NA_ID = '__NA__';

// ============================================================================
// BOOKING API
// ============================================================================

export async function createBooking(input: CreateBookingInput): Promise<StudioBooking> {
  const res = await api.post('/api/studio/bookings', input);
  return res.data.booking;
}

export async function listMyBookings(): Promise<StudioBooking[]> {
  const res = await api.get('/api/studio/bookings/my');
  return res.data.bookings;
}

export async function cancelBooking(bookingId: string): Promise<StudioBooking> {
  const res = await api.patch(`/api/studio/bookings/${bookingId}/cancel`);
  return res.data.booking;
}

export async function listReviewQueue(): Promise<StudioBooking[]> {
  const res = await api.get('/api/studio/bookings/review');
  return res.data.bookings;
}

export async function listAllBookings(): Promise<StudioBooking[]> {
  const res = await api.get('/api/studio/bookings/all');
  return res.data.bookings;
}

export async function getPendingCount(): Promise<number> {
  const res = await api.get('/api/studio/bookings/pending-count');
  return res.data.count;
}

export async function updateBookingMetadata(
  bookingId: string,
  updates: { numberOfCustomers?: number; numberOfAttendees?: number }
): Promise<StudioBooking> {
  const res = await api.patch(`/api/studio/bookings/${bookingId}/metadata`, updates);
  return res.data.booking;
}

export async function updateBookingDetails(
  bookingId: string,
  updates: {
    roomId?: string;
    facilitatorId?: string;
    secondaryFacilitatorIds?: string[];
    accountTeamLeadEmail?: string;
  sendAccountManagerEmails?: boolean;
    traineeCandidateIds?: string[];
    customer?: string;
    sector?: string;
    startDate?: string;
    endDate?: string;
    dateType?: BookingDateType;
    goal?: string;
    overrideOutlookConflict?: boolean;
  }
): Promise<StudioBooking> {
  const res = await api.patch(`/api/studio/bookings/${bookingId}/details`, updates);
  return res.data.booking;
}

export async function reviewBooking(
  bookingId: string,
  action: 'approved' | 'rejected',
  notes?: string,
  forceWithoutOutlookSync?: boolean
): Promise<StudioBooking> {
  const res = await api.patch(`/api/studio/bookings/${bookingId}/review`, { action, notes, forceWithoutOutlookSync });
  return res.data.booking;
}

export async function recordTraineeAttendance(bookingId: string, attendedCandidateIds: string[]): Promise<StudioBooking> {
  const res = await api.post(`/api/studio/bookings/${bookingId}/attendance`, { attendedCandidateIds });
  return res.data.booking;
}

export async function retryOutlookSync(bookingId: string): Promise<StudioBooking> {
  const res = await api.post(`/api/studio/bookings/${bookingId}/retry-outlook-sync`);
  return res.data.booking;
}

export interface ExternalBooking {
  roomId: string;
  roomName: string;
  startDateTime: string;
  endDateTime: string;
}

// Rooms booked directly in Outlook, outside the portal — no title available,
// Graph's free/busy API never returns another mailbox's event details.
export async function listExternalBookings(startDate: string, endDate: string): Promise<ExternalBooking[]> {
  const res = await api.get(`/api/studio/bookings/external?startDate=${startDate}&endDate=${endDate}`);
  return res.data.externalBookings;
}

// ============================================================================
// EVENT TYPES API
// ============================================================================

export async function listEventTypes(): Promise<EventType[]> {
  const res = await api.get('/api/studio/event-types');
  return res.data.eventTypes;
}

export async function listAllEventTypes(): Promise<EventType[]> {
  const res = await api.get('/api/studio/event-types/all');
  return res.data.eventTypes;
}

export async function createEventType(name: string, description?: string, isFacilitatorTraining?: boolean): Promise<EventType> {
  const res = await api.post('/api/studio/event-types', { name, description, isFacilitatorTraining });
  return res.data.eventType;
}

export async function updateEventType(
  eventTypeId: string,
  updates: Partial<Pick<EventType, 'name' | 'description' | 'isActive' | 'isFacilitatorTraining' | 'whatToExpect'>>
): Promise<EventType> {
  const res = await api.put(`/api/studio/event-types/${eventTypeId}`, updates);
  return res.data.eventType;
}

export async function deleteEventType(eventTypeId: string): Promise<void> {
  await api.delete(`/api/studio/event-types/${eventTypeId}`);
}

// ============================================================================
// SECTORS API
// ============================================================================

export async function listSectors(): Promise<Sector[]> {
  const res = await api.get('/api/studio/sectors');
  return res.data.sectors;
}

export async function listAllSectors(): Promise<Sector[]> {
  const res = await api.get('/api/studio/sectors/all');
  return res.data.sectors;
}

export async function createSector(name: string): Promise<Sector> {
  const res = await api.post('/api/studio/sectors', { name });
  return res.data.sector;
}

export async function updateSector(
  sectorId: string,
  updates: Partial<Pick<Sector, 'name' | 'isActive'>>
): Promise<Sector> {
  const res = await api.put(`/api/studio/sectors/${sectorId}`, updates);
  return res.data.sector;
}

export async function deleteSector(sectorId: string): Promise<void> {
  await api.delete(`/api/studio/sectors/${sectorId}`);
}

// ============================================================================
// FACILITATORS API
// ============================================================================

export async function listFacilitators(): Promise<FacilitatorSummary[]> {
  const res = await api.get('/api/studio/facilitators');
  return res.data.facilitators;
}

export async function certifyUser(userId: string, tier: FacilitatorTier): Promise<void> {
  await api.post(`/api/studio/facilitators/${userId}/certify`, { tier });
}

export async function revokeUserCertification(userId: string): Promise<void> {
  await api.delete(`/api/studio/facilitators/${userId}/certify`);
}

// ============================================================================
// FACILITATOR CANDIDATES — people interested in becoming a facilitator
// ============================================================================

export type FacilitatorCandidateSource = 'self' | 'nominated';

export interface FacilitatorCandidate {
  candidateId: string;
  userId?: string;
  name: string;
  email: string;
  source: FacilitatorCandidateSource;
  addedBy: string;
  addedByName: string;
  addedAt: string;
  notes?: string;
  trainingAttendedCount: number;
}

export interface SelectableCandidate {
  candidateId: string;
  name: string;
}

export type NominateCandidateInput = { userId: string; notes?: string } | { name: string; email: string; notes?: string };

/** Tier 1 / system admin only. */
export async function listFacilitatorCandidates(): Promise<FacilitatorCandidate[]> {
  const res = await api.get('/api/studio/facilitator-candidates');
  return res.data.candidates;
}

/** Names only, for inviting trainees to a facilitator training booking. */
export async function listSelectableCandidates(): Promise<SelectableCandidate[]> {
  const res = await api.get('/api/studio/facilitator-candidates/selectable');
  return res.data.candidates;
}

export async function searchNominableUsers(q: string): Promise<Array<{ userId: string; displayName: string; email: string }>> {
  const res = await api.get('/api/studio/facilitator-candidates/user-search', { params: { q } });
  return res.data.users;
}

export async function nominateCandidate(input: NominateCandidateInput): Promise<void> {
  await api.post('/api/studio/facilitator-candidates', input);
}

export async function removeFacilitatorCandidate(candidateId: string): Promise<void> {
  await api.delete(`/api/studio/facilitator-candidates/${candidateId}`);
}

export async function getMyFacilitatorInterest(): Promise<{ interested: boolean; addedAt?: string; source?: FacilitatorCandidateSource }> {
  const res = await api.get('/api/studio/me/facilitator-interest');
  return res.data;
}

export async function setMyFacilitatorInterest(interested: boolean): Promise<void> {
  if (interested) await api.put('/api/studio/me/facilitator-interest');
  else await api.delete('/api/studio/me/facilitator-interest');
}

// ============================================================================
// FACILITATOR REPORT, SECTORS AND TRAINING RECORD
// ============================================================================

export type FacilitatorTrainingStatus =
  | 'not_started'
  | 'walkthrough_complete'
  | 'trained'
  | 'assessment_failed'
  | 'assessed'
  | 'reassessment_due'
  | 'overdue';

export type FacilitatorTrainingEventType = 'in_person_training' | 'assessment';
export type AssessmentOutcome = 'pass' | 'fail';

export interface SectorRef {
  sectorId: string;
  name: string;
  isActive: boolean;
}

export interface FacilitatorTraining {
  walkthroughCompletedAt?: string;
  inPersonTrainedAt?: string;
  assessedAt?: string;
  assessmentOutcome?: AssessmentOutcome;
  nextAssessmentDue?: string;
  status: FacilitatorTrainingStatus;
}

export interface FacilitatorReportRow extends FacilitatorSummary {
  primarySector: SectorRef | null;
  secondarySectors: SectorRef[];
  bookings: { leadAllTime: number; leadLast12m: number; supportAllTime: number; supportLast12m: number };
  nextBooking: {
    bookingId: string;
    startDate: string;
    endDate: string;
    customer: string;
    roomName: string;
    role: 'lead' | 'support';
  } | null;
  training: FacilitatorTraining;
}

export interface FacilitatorTrainingEvent {
  eventId: string;
  userId: string;
  type: FacilitatorTrainingEventType;
  date: string;
  outcome?: AssessmentOutcome;
  notes?: string;
  recordedBy: string;
  recordedByName: string;
  recordedAt: string;
}

export interface MyFacilitatorProfile {
  primarySector: SectorRef | null;
  secondarySectors: SectorRef[];
  training: FacilitatorTraining;
  events: FacilitatorTrainingEvent[];
}

export const TRAINING_STATUS_LABELS: Record<FacilitatorTrainingStatus, string> = {
  not_started: 'Not started',
  walkthrough_complete: 'Walkthrough done',
  trained: 'Trained',
  assessment_failed: 'Assessment failed',
  assessed: 'Assessed',
  reassessment_due: 'Reassessment due',
  overdue: 'Reassessment overdue'
};

export const TRAINING_STATUS_COLORS: Record<FacilitatorTrainingStatus, string> = {
  not_started: 'bg-slate-100 text-slate-500 border-slate-200',
  walkthrough_complete: 'bg-sky-50 text-sky-700 border-sky-200',
  trained: 'bg-blue-50 text-blue-700 border-blue-200',
  assessment_failed: 'bg-red-50 text-red-700 border-red-200',
  assessed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  reassessment_due: 'bg-amber-50 text-amber-700 border-amber-200',
  overdue: 'bg-red-50 text-red-700 border-red-200'
};

export const TRAINING_EVENT_LABELS: Record<FacilitatorTrainingEventType, string> = {
  in_person_training: 'In-person training',
  assessment: 'Assessment'
};

export async function getFacilitatorReport(): Promise<{ facilitators: FacilitatorReportRow[]; canManage: boolean }> {
  const res = await api.get('/api/studio/facilitators/report');
  return res.data;
}

export async function getMyFacilitatorProfile(): Promise<MyFacilitatorProfile> {
  const res = await api.get('/api/studio/me/facilitator-profile');
  return res.data;
}

export async function updateMySectors(
  primarySectorId: string | null,
  secondarySectorIds: string[]
): Promise<{ primarySector: SectorRef | null; secondarySectors: SectorRef[] }> {
  const res = await api.patch('/api/studio/me/sectors', { primarySectorId, secondarySectorIds });
  return res.data;
}

export async function listTrainingEvents(userId: string): Promise<FacilitatorTrainingEvent[]> {
  const res = await api.get(`/api/studio/facilitators/${userId}/training`);
  return res.data.events;
}

export async function addTrainingEvent(
  userId: string,
  input: { type: FacilitatorTrainingEventType; date: string; outcome?: AssessmentOutcome; notes?: string }
): Promise<void> {
  await api.post(`/api/studio/facilitators/${userId}/training`, input);
}

export async function deleteTrainingEvent(userId: string, eventId: string): Promise<void> {
  await api.delete(`/api/studio/facilitators/${userId}/training/${eventId}`);
}

// ============================================================================
// BLOCKED DAYS API
// ============================================================================

export interface BlockedDay {
  date: string;       // YYYY-MM-DD
  reason?: string;
  organizationId: string;
  createdBy: string;
  createdByName: string;
  createdAt: string;
}

export async function listBlockedDays(year: number, month: number): Promise<BlockedDay[]> {
  const res = await api.get('/api/studio/blocked-days', { params: { year, month } });
  return res.data.blockedDays;
}

export async function setBlockedDay(date: string, reason?: string): Promise<void> {
  await api.put(`/api/studio/blocked-days/${date}`, { reason });
}

export async function removeBlockedDay(date: string): Promise<void> {
  await api.delete(`/api/studio/blocked-days/${date}`);
}

// ============================================================================
// FACILITATOR AVAILABILITY — Tier 1 facilitators, per studio per half-day
// ============================================================================

export type AvailabilitySlot = 'morning' | 'afternoon' | 'full_day';
export type HalfDay = 'morning' | 'afternoon';
export type Weekday = 'mon' | 'tue' | 'wed' | 'thu' | 'fri';

export interface SlotSummary {
  free: number;
  names?: string[];   // absent for guests
  roomBooked: boolean;
}

export interface AvailabilitySummary {
  days: Record<string, Record<string, Record<HalfDay, SlotSummary>>>;  // date -> roomId -> half-day
  unavailable?: Record<string, Record<string, AvailabilitySlot>>;     // userId -> date -> slot
  unassigned?: { userId: string; displayName: string }[];
}

export interface AvailabilityCheck {
  shortfalls: { date: string; slot: HalfDay }[];
  conflicts: Record<string, 'unavailable' | 'booked'>;
}

export interface AvailabilityException {
  userId: string;
  date: string;
  unavailable: AvailabilitySlot | 'none';
  reason?: string;
  updatedBy: string;
  updatedAt: string;
}

export interface UserAvailability {
  userId: string;
  homeRoomIds: string[];
  weekdays: Partial<Record<Weekday, AvailabilitySlot>>;
  exceptions: AvailabilityException[];
}

export async function getAvailabilitySummary(year: number, month: number): Promise<AvailabilitySummary> {
  const res = await api.get('/api/studio/availability/summary', { params: { year, month } });
  return res.data;
}

export async function checkFacilitatorAvailability(params: {
  roomId: string; startDate: string; endDate?: string; dateType: BookingDateType; excludeBookingId?: string;
}): Promise<AvailabilityCheck> {
  const res = await api.get('/api/studio/availability/check', { params });
  return res.data;
}

export async function getUserAvailability(userId: string, year: number, month: number): Promise<UserAvailability> {
  const res = await api.get(`/api/studio/availability/users/${userId}`, { params: { year, month } });
  return res.data;
}

export async function setWeeklyAvailability(userId: string, weekdays: UserAvailability['weekdays']): Promise<void> {
  await api.put(`/api/studio/availability/users/${userId}/rule`, { weekdays });
}

// unavailable: null clears the range back to the weekly pattern; 'none' marks working days
export async function setAvailabilityDays(userId: string, body: {
  startDate: string; endDate?: string; unavailable: AvailabilitySlot | 'none' | null; reason?: string;
}): Promise<void> {
  await api.put(`/api/studio/availability/users/${userId}/days`, body);
}

export async function setHomeStudios(userId: string, roomIds: string[]): Promise<string[]> {
  const res = await api.patch(`/api/studio/availability/users/${userId}/home-studios`, { roomIds });
  return res.data.homeRoomIds;
}
